/**
 * Sales Orders CopilotKit Actions
 * AI-executable actions for sales orders
 */

import { useCopilotAction } from '@copilotkit/react-core'
import { useDispatch } from '@ufc/core'
import { salesOrdersActions } from '../store/sales-orders.slice'

export function useSalesOrdersActions() {
  const dispatch = useDispatch()

  useCopilotAction({
    name: 'filterSalesOrdersByStatus',
    description: 'Filter sales orders by their status (active, completed, draft)',
    parameters: [
      {
        name: 'status',
        type: 'string',
        description: 'Status to filter by: active, completed, draft, or all',
        required: true,
      },
    ],
    handler: async ({ status }) => {
      dispatch(salesOrdersActions.filterByStatus(status as 'active' | 'completed' | 'draft' | 'all'))
      return `Filtered sales orders to show ${status === 'all' ? 'all sales orders' : `${status} sales orders`}`
    },
  })

  useCopilotAction({
    name: 'filterSalesOrdersByType',
    description: 'Filter sales orders by type (supply, service, framework, spot)',
    parameters: [
      {
        name: 'type',
        type: 'string',
        description: 'Type to filter by: supply, service, framework, spot, or all',
        required: true,
      },
    ],
    handler: async ({ type }) => {
      dispatch(salesOrdersActions.filterByType(type as 'supply' | 'service' | 'framework' | 'spot' | 'all'))
      return `Filtered sales orders to show ${type === 'all' ? 'all types' : `${type} sales orders`}`
    },
  })

  useCopilotAction({
    name: 'searchSalesOrders',
    description: 'Search sales orders by name or order number',
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'Search query to find sales orders',
        required: true,
      },
    ],
    handler: async ({ query }) => {
      dispatch(salesOrdersActions.searchSalesOrders(query))
      return `Searching sales orders for "${query}"`
    },
  })

  useCopilotAction({
    name: 'renewSalesOrder',
    description: 'Renew a sales order with a new end date',
    parameters: [
      {
        name: 'salesOrderId',
        type: 'string',
        description: 'ID of the sales order to renew',
        required: true,
      },
      {
        name: 'newEndDate',
        type: 'string',
        description: 'New end date for the sales order (YYYY-MM-DD format)',
        required: true,
      },
    ],
    handler: async ({ salesOrderId, newEndDate }) => {
      dispatch(salesOrdersActions.renewSalesOrder({ salesOrderId, newEndDate }))
      return `Sales order renewal initiated for ${salesOrderId} with new end date ${newEndDate}`
    },
  })
}

// Legacy alias for backwards compatibility
export const useContractsActions = useSalesOrdersActions
